import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import {pluralize} from 'BenchlingUtil';
import {ActionOrigin, TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeTestOrderBulkEventEventMetadata,
  TEST_ORDER_BULK_UPDATE_ENDED_EVENT,
  TEST_ORDER_BULK_UPDATE_STARTED_EVENT,
} from 'performance/processSampleTestsEvents';
import {bulkUpdateTestOrders} from 'sample-tests/network/TestOrderDAO';
import {TestOrderDataModAction} from 'sample-tests/test-orders/TestOrderDomainTypes';
import TestOrderDisplayTable from 'sample-tests/ui/test-orders/modals/TestOrderDisplayTable';
import {TestOrderStatusDropdown} from 'sample-tests/ui/test-orders/TestOrderStatusDropdown';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {
  RowSelectionMode,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {TestOrderListingGraphQL} from '../../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';

export interface BulkUpdateTestOrderStatusModalProps {
  testOrders: Array<TestOrderListingGraphQL>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function BulkUpdateTestOrderStatusModal(
  props: BulkUpdateTestOrderStatusModalProps
): JSX.Element {
  const {testOrders, onRequestClose, onRequestSubmit} = props;
  const [newStatus, setNewStatus] = useState<TestOrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTestOrderIds, setSelectedTestOrderIds] = useState<Set<string>>(
    new Set(testOrders.map((testOrder) => testOrder.id))
  );
  const testOrdersString = `${selectedTestOrderIds.size} test ${pluralize(selectedTestOrderIds.size, 'order')}`;

  function updateTestOrdersWithDataModifications(): Promise<void> {
    assert(newStatus != null, 'Expected submit to be disabled for null status');

    setError(null);
    return callWithDataModifications({
      changeSummary: `Updating ${testOrdersString}`,
      onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
        try {
          logProfilerTimingEvent(TEST_ORDER_BULK_UPDATE_STARTED_EVENT);
          await bulkUpdateTestOrders(
            [...selectedTestOrderIds],
            {status: newStatus},
            {actionOrigin: ActionOrigin.SAMPLE_TESTS_BULK_UPDATE_TEST_ORDER_STATUS_MODAL},
            dataModsHeader
          );
          logProfilerTimingEvent(
            TEST_ORDER_BULK_UPDATE_ENDED_EVENT,
            composeTestOrderBulkEventEventMetadata(
              testOrders.length,
              ActionOrigin.SAMPLE_TESTS_BULK_UPDATE_TEST_ORDER_STATUS_MODAL
            )
          );
          notifications.success(`${testOrdersString} updated`);
          onRequestSubmit();
        } catch (err) {
          const friendlyError = notifications.getFriendlyError(err);
          if (friendlyError != null) {
            setError(friendlyError);
          } else {
            throw err;
          }
        }
      },
      apiIds: [...selectedTestOrderIds],
      analyticsParams: {
        module: 'SAMPLE_TESTS',
        actionOrigin: ActionOrigin.SEARCH_EXPANDED_SEARCH_TABLE,
        modifiedAction: TestOrderDataModAction.STATUS_CHANGE,
        numModifiedTotalItems: selectedTestOrderIds.size,
        entryId: null,
      },
      useProjectLevelDataModsLogic: true,
    });
  }

  const onSelectRows = (affectedRows: Array<ScrollableTableRow>, newIsSelected: boolean): void => {
    const affectedTestOrderIds = new Set(affectedRows.map(({id}) => id));
    const newSelectedTestOrderIds = newIsSelected
      ? new Set([...selectedTestOrderIds, ...affectedTestOrderIds])
      : new Set([...selectedTestOrderIds].filter((key) => !affectedTestOrderIds.has(key)));
    setSelectedTestOrderIds(newSelectedTestOrderIds);
  };

  return (
    <Modal
      title={<div className={css(styles.modalTitle)}>Update status</div>}
      mediumLarge={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={updateTestOrdersWithDataModifications}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Update', submit: true, disabled: newStatus == null || selectedTestOrderIds.size === 0},
      ]}
      closeable={true}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
      expandHeight={true}
    >
      <FieldLabel isRequired={true} label="New status" />
      <TestOrderStatusDropdown status={newStatus} setStatus={setNewStatus} />
      {error != null && (
        <div className={css(styles.errorBanner)}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />
        </div>
      )}
      <div className={css(styles.modalText)}>
        {`Are you sure you'd like to update the status for the following ${testOrdersString}?`}
      </div>
      <TestOrderDisplayTable
        testOrders={testOrders}
        tableKey={TABLE_KEYS.UPDATE_TEST_ORDER_STATUS_TABLE}
        selectionProps={{
          rowSelectionMode: RowSelectionMode.MULTIPLE,
          onRowsSelect: onSelectRows,
          selectedTestOrderIds,
          isSelected: (testOrderId) => selectedTestOrderIds.has(testOrderId),
        }}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalText: {
    fontSize: '13px',
    fontWeight: 400,
    paddingBottom: '16px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 400,
  },
  modalColumnHeader: {
    fontSize: '13px',
    fontWeight: 600,
  },
  errorBanner: {
    marginBottom: '12px',
  },
});
